import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import './ServiceDetailPage.css';

/* ─────────────────────────────────────────────────────────────
   Full content for each service — trust-building copy
───────────────────────────────────────────────────────────── */
const serviceData = {
  'buy-sell-property': {
    title: 'Buy Or Sell Property',
    tagline: 'Your Trusted Partner for Every Property Transaction',
    icon: 'fa-house',
    heroColor: 'linear-gradient(135deg,#0f2027,#203a43,#2c5364)',
    image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=80',
    intro: `Buying or selling a property is one of the most significant financial decisions of your life. 
At BricksNHome, we ensure every step — from the first site visit to the final registry — is smooth, 
transparent, and in your best interest.`,
    whyUs: [
      { icon: '🏅', title: '15+ Years of Market Expertise', desc: 'Deep knowledge of Greater Noida West, Yamuna Expressway, and NCR micro-markets. We know which localities are appreciating and which to avoid.' },
      { icon: '✅', title: 'Verified Listings Only', desc: 'Every property listed with us is RERA-registered, legally clear, and physically verified by our team before you ever visit.' },
      { icon: '💰', title: 'Best Price Guarantee', desc: 'Our negotiation experts ensure you never overpay as a buyer, and never undersell as a seller. We have data on every comparable transaction.' },
      { icon: '🤝', title: 'End-to-End Support', desc: 'From shortlisting to loan processing, registry, and possession — we are with you at every milestone, not just the sale.' },
      { icon: '📋', title: 'Zero Hidden Charges', desc: 'Our fee structure is completely transparent. What you see is what you pay. No surprise costs, ever.' },
      { icon: '🔒', title: 'Legal Title Verification', desc: 'Our in-house legal team verifies title documents, NOCs, and encumbrances before you commit a single rupee.' },
    ],
    process: [
      { step: '01', title: 'Free Consultation', desc: 'Tell us your requirements, budget, and preferred location. No cost, no obligation.' },
      { step: '02', title: 'Property Shortlisting', desc: 'We curate a verified list matching your needs — visiting every property before recommending it.' },
      { step: '03', title: 'Site Visits', desc: 'Accompanied site visits with our expert agent who knows the neighbourhood, builder history, and future development plans.' },
      { step: '04', title: 'Price Negotiation', desc: 'We negotiate on your behalf using real transaction data to ensure the fairest deal.' },
      { step: '05', title: 'Documentation & Loan', desc: 'Complete legal due diligence + home loan assistance with our banking partners.' },
      { step: '06', title: 'Registry & Possession', desc: 'We accompany you to the sub-registrar office and hand over keys — your journey complete.' },
    ],
    testimonials: [
      { name: 'Rajesh Sharma', location: 'Bought 4BHK at Krishna Vatika', text: '"BricksNHome helped me find a RERA-registered villa within my budget. The entire process — from site visit to registry — took just 45 days. Absolutely seamless."' },
      { name: 'Priya Mehta', location: 'Sold flat in Noida Extension', text: '"I had been trying to sell my apartment for 6 months with other agents. BricksNHome closed the deal in 3 weeks at a price higher than my expectation."' },
      { name: 'Amit Gupta', location: 'Invested in Bollywood Aero City', text: '"Their investment advice on Yamuna Expressway plots was spot on. The value has already appreciated 28% in under a year."' },
    ],
    cta: 'Book a Free Consultation',
    stats: [{ n: '8,500+', l: 'Happy Clients' }, { n: '15+', l: 'Years Experience' }, { n: '₹0', l: 'Hidden Charges' }, { n: '100%', l: 'RERA Verified' }],
  },

  'rent-lease-property': {
    title: 'Rent Or Lease Property',
    tagline: 'Premium Rentals. Zero Headache. Assured Returns.',
    icon: 'fa-key',
    heroColor: 'linear-gradient(135deg,#1a1a2e,#16213e,#0f3460)',
    image: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=1200&q=80',
    intro: `Whether you are a tenant looking for a quality home or a landlord wanting assured, hassle-free rental income — 
BricksNHome is the name Greater Noida West trusts. Our rental network spans 500+ verified properties with zero brokerage surprises.`,
    whyUs: [
      { icon: '📅', title: 'Rental Starts from Day 1', desc: 'For investment properties like Paradise Greens Plaza, assured rental income begins the day you take possession. No waiting period.' },
      { icon: '✅', title: 'Verified Tenants & Owners', desc: 'Every tenant is background-checked. Every owner listing is physically verified. We never show you paper properties.' },
      { icon: '📈', title: 'Assured Rental @ ₹40/sq.ft', desc: 'Our commercial studio apartments offer assured rental returns of ₹40 per sq.ft per month — backed by a contractual agreement.' },
      { icon: '⚖️', title: 'Legally Sound Agreements', desc: 'All rental agreements are drafted by our legal team — compliant with local laws, protecting both tenant and landlord.' },
      { icon: '🛠️', title: 'Property Management', desc: 'For NRI and outstation landlords, we offer complete property management — maintenance, rent collection, and tenant communication.' },
      { icon: '🏘️', title: '500+ Active Listings', desc: 'Largest rental inventory in Greater Noida West with 1BHK, 2BHK, villas, studio apartments, and commercial spaces.' },
    ],
    process: [
      { step: '01', title: 'Share Your Requirement', desc: 'Tell us your budget, preferred area, property type, and move-in date.' },
      { step: '02', title: 'Curated Property List', desc: 'We share 5–7 shortlisted properties — all verified, all within your spec.' },
      { step: '03', title: 'Site Visit', desc: 'Our agent accompanies you, highlighting the pros and cons of each property honestly.' },
      { step: '04', title: 'Agreement Drafting', desc: 'Legal rental agreement prepared with all terms clearly defined.' },
      { step: '05', title: 'Move-In Assistance', desc: 'Key handover, utility transfer, and a smooth move-in experience.' },
    ],
    testimonials: [
      { name: 'Sunita Verma', location: 'Renting 2BHK in Greater Noida West', text: '"Got a fully verified flat with no brokerage games. The agreement was crystal clear and the agent helped us even after we moved in."' },
      { name: 'Prakash Nair', location: 'Landlord, 3 properties', text: '"BricksNHome manages all my rental properties. Rent is always on time. Zero tenant issues in 2 years."' },
    ],
    cta: 'Find Rental Properties',
    stats: [{ n: '1,075+', l: 'Properties Leased' }, { n: '₹40/sqft', l: 'Assured Rental' }, { n: '500+', l: 'Active Listings' }, { n: '2 Yrs', l: 'Average Tenancy' }],
  },

  'commercial-properties': {
    title: 'Commercial Properties',
    tagline: 'Prime Commercial Spaces. Maximum Business Visibility.',
    icon: 'fa-building',
    heroColor: 'linear-gradient(135deg,#0f0c29,#302b63,#24243e)',
    image: 'https://images.unsplash.com/photo-1486325212027-8081e485255e?w=1200&q=80',
    intro: `The right commercial location is not a cost — it is your biggest competitive advantage. 
BricksNHome has the deepest inventory of retail shops, office spaces, and commercial plots 
across Noida Extension's highest-footfall corridors.`,
    whyUs: [
      { icon: '📍', title: 'Prime Locations on 60m Roads', desc: 'All our commercial listings are on wide arterial roads ensuring maximum visibility, walk-in traffic, and signage exposure.' },
      { icon: '🏪', title: 'Retail Shops from ₹23.90 Lac', desc: 'Paradise Greens Plaza offers ground-floor retail shops starting ₹23.90 Lac — with an existing residential catchment above the commercial floors.' },
      { icon: '🔑', title: 'Ready to Move', desc: 'All featured commercial spaces are construction-complete. You can open your business without waiting for builders.' },
      { icon: '📈', title: 'High ROI Potential', desc: 'Commercial properties in Noida Extension are seeing 12–18% rental yield — far outperforming residential properties.' },
      { icon: '🏗️', title: 'RERA Registered Projects', desc: 'Every commercial project is RERA compliant — protecting your investment from builder delays and legal disputes.' },
      { icon: '🤝', title: 'Corporate Leasing Support', desc: 'Looking for office space for your company? We handle corporate lease agreements with full legal support.' },
    ],
    process: [
      { step: '01', title: 'Business Requirement Analysis', desc: 'We understand your business, footfall needs, storage requirements, and visibility goals.' },
      { step: '02', title: 'Location Strategy', desc: 'Our commercial experts suggest the best micro-markets based on your customer demographics.' },
      { step: '03', title: 'Property Shortlisting', desc: 'Verified, ready-to-move commercial spaces matching your size and budget.' },
      { step: '04', title: 'Due Diligence', desc: 'Legal verification, NOC checks, commercial zoning confirmation.' },
      { step: '05', title: 'Deal Closure', desc: 'Negotiation, agreement, and possession — handled end-to-end.' },
    ],
    testimonials: [
      { name: 'Vikram Joshi', location: 'Retail shop at Paradise Greens Plaza', text: '"I opened my pharmacy on the ground floor. Walk-ins started from Week 1 because of the residential complex above. Best commercial investment I have made."' },
      { name: 'Neha Singh', location: 'Office Space, Noida Extension', text: '"BricksNHome found us an office within 10 days. The legal team handled everything — we just had to show up and sign."' },
    ],
    cta: 'Explore Commercial Spaces',
    stats: [{ n: '50+', l: 'Commercial Projects' }, { n: '12–18%', l: 'Rental Yield' }, { n: '₹23.9L', l: 'Starting Price' }, { n: '100%', l: 'RERA Compliant' }],
  },

  'investment-advisory': {
    title: 'Investment Advisory',
    tagline: 'Smarter Real Estate Investments. Proven ROI.',
    icon: 'fa-chart-line',
    heroColor: 'linear-gradient(135deg,#134e5e,#71b280)',
    image: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1200&q=80',
    intro: `Real estate is the most reliable wealth-building asset in India. But not every property 
appreciates equally. BricksNHome's investment advisory team uses real market data, 
infrastructure intelligence, and 15 years of transaction history to guide you toward 
properties that consistently outperform the market.`,
    whyUs: [
      { icon: '📊', title: 'Data-Driven Recommendations', desc: 'We analyse RERA data, infrastructure announcements, metro corridors, and comparable sales before recommending any investment.' },
      { icon: '✈️', title: 'Airport Corridor Advantage', desc: 'Our Bollywood Aero City Farms near Jewar International Airport is positioned at a once-in-a-generation infrastructure nexus.' },
      { icon: '📈', title: 'Projected 200–300% Appreciation', desc: 'YEIDA region properties near the airport corridor are projected to appreciate 200–300% by 2030 according to market reports.' },
      { icon: '🏆', title: '7+ Real Estate Awards', desc: 'Recognised by industry bodies for excellence in investment advisory and client wealth creation.' },
      { icon: '💼', title: 'Portfolio Diversification', desc: 'We advise on balancing residential plots, commercial spaces, and farm estates to maximise overall portfolio returns.' },
      { icon: '🔄', title: 'Exit Strategy Planning', desc: 'We help you plan not just when to buy — but when and how to sell for maximum profit.' },
    ],
    process: [
      { step: '01', title: 'Portfolio Assessment', desc: 'We understand your current investments, risk appetite, and financial goals.' },
      { step: '02', title: 'Market Intelligence Report', desc: 'Customised report on top-performing micro-markets matching your investment horizon.' },
      { step: '03', title: 'Property Recommendation', desc: '2–3 high-conviction property recommendations with full financial projections.' },
      { step: '04', title: 'Due Diligence', desc: 'Legal, financial, and physical verification of shortlisted properties.' },
      { step: '05', title: 'Investment Execution', desc: 'End-to-end support from booking to registration.' },
      { step: '06', title: 'Portfolio Monitoring', desc: 'Ongoing market updates and optimal sell-timing advice.' },
    ],
    testimonials: [
      { name: 'Rohit Agarwal', location: 'Invested in Yamuna Expressway plots', text: '"The team identified the Jewar corridor before it became mainstream news. My plot has appreciated 38% in 18 months. Best financial decision of my life."' },
      { name: 'Kavya Reddy', location: 'Diversified portfolio across 3 projects', text: '"They advised me to split my investment across a villa, a studio apartment, and a farm plot. The rental income alone covers my EMI."' },
    ],
    cta: 'Schedule Investment Consultation',
    stats: [{ n: '38%+', l: 'Avg. Appreciation' }, { n: '₹200Cr+', l: 'Assets Managed' }, { n: '7+', l: 'Industry Awards' }, { n: '34+', l: 'Builder Tie-ups' }],
  },

  'home-loan-assistance': {
    title: 'Home Loan Assistance',
    tagline: 'Best Rate. Fast Approval. Zero Paperwork Stress.',
    icon: 'fa-hand-holding-dollar',
    heroColor: 'linear-gradient(135deg,#1d4350,#a43931)',
    image: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=1200&q=80',
    intro: `Getting a home loan approved at the best interest rate requires insider knowledge of 
bank processes and underwriting criteria. BricksNHome has tie-ups with 12+ leading banks 
and NBFCs to ensure you get the highest loan amount at the lowest possible rate — with minimal documentation.`,
    whyUs: [
      { icon: '🏦', title: 'Tie-ups with 12+ Banks', desc: 'SBI, HDFC, ICICI, Axis, Bank of Baroda, LIC Housing, PNB Housing and more — we compare rates across all and recommend the best.' },
      { icon: '⚡', title: 'Pre-Approved Loans Available', desc: 'For select projects like Krishna Vatika and Lotus Greens, pre-approved loan offers are available — shortening your timeline dramatically.' },
      { icon: '💯', title: 'Up to 90% Loan-to-Value', desc: 'Our banking relationships allow us to secure higher LTV ratios than you would get applying independently.' },
      { icon: '📄', title: 'Minimal Documentation', desc: 'We handle all paperwork — application, income proofs, property documents — on your behalf. You just sign.' },
      { icon: '🔁', title: 'Balance Transfer Advisory', desc: 'Already have a high-rate home loan? We help you identify and execute balance transfers that can save lakhs over the loan tenure.' },
      { icon: '✅', title: '95%+ Approval Rate', desc: 'Our pre-screening and bank relationship management results in a loan approval rate that far exceeds the industry average.' },
    ],
    process: [
      { step: '01', title: 'Eligibility Check', desc: 'Free assessment of your loan eligibility based on income, credit score, and existing obligations.' },
      { step: '02', title: 'Bank Comparison', desc: 'We compare rates, processing fees, and conditions across 12+ lenders in real time.' },
      { step: '03', title: 'Document Collection', desc: 'Our team collects and organises all required documents — no running around.' },
      { step: '04', title: 'Application Filing', desc: 'We file with the optimal lender and follow up daily until sanction.' },
      { step: '05', title: 'Disbursement', desc: 'Coordinating with the bank and builder for timely fund disbursement at every construction stage.' },
    ],
    testimonials: [
      { name: 'Sanjay Kumar', location: 'Home Loan for Aalayam Srishti Villa', text: '"I was rejected by two banks on my own. BricksNHome restructured my application and got me approved at 8.5% within 12 days. Exceptional service."' },
      { name: 'Meena Pillai', location: 'Refinanced ₹45L loan', text: '"They identified that I was overpaying ₹3,200/month on my existing loan. The balance transfer saved me ₹8.7 Lac over the remaining tenure."' },
    ],
    cta: 'Check Your Loan Eligibility',
    stats: [{ n: '12+', l: 'Bank Partners' }, { n: '95%+', l: 'Approval Rate' }, { n: '90%', l: 'Max LTV' }, { n: '₹0', l: 'Consultation Fee' }],
  },

  'legal-registration': {
    title: 'Legal & Registration',
    tagline: 'Your Property. Completely Protected. Legally Watertight.',
    icon: 'fa-file-contract',
    heroColor: 'linear-gradient(135deg,#200122,#6f0000)',
    image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200&q=80',
    intro: `Real estate fraud and disputed titles are real risks when buying property in India. 
BricksNHome's in-house legal team has processed 1,200+ property registrations with zero 
title dispute cases. We protect your investment from every angle — before, during, and after the transaction.`,
    whyUs: [
      { icon: '🔍', title: 'Title Verification', desc: 'We verify 30 years of title history, check for encumbrances, mortgages, litigation, and multiple ownership claims before you pay anything.' },
      { icon: '📋', title: 'RERA Compliance Check', desc: 'Verification that the project, builder, and agent are all registered under RERA — your legal protection against builder fraud.' },
      { icon: '✍️', title: 'Agreement Drafting', desc: 'Sale agreements, lease deeds, GPA, and all property documents drafted by qualified lawyers with your interest protected.' },
      { icon: '🏛️', title: 'Registration Assistance', desc: 'Full support at the sub-registrar office — appointment booking, document preparation, stamp duty calculation, and accompaniment.' },
      { icon: '🛡️', title: 'Mutation & Transfer', desc: 'Post-registration mutation in revenue records, property tax transfer, and utility name change — all handled by our team.' },
      { icon: '⚖️', title: 'Dispute Resolution', desc: 'In the rare event of a property dispute, our legal network provides priority access to experienced real estate advocates.' },
    ],
    process: [
      { step: '01', title: 'Document Collection', desc: 'Collecting all seller documents — title deed, encumbrance certificate, tax receipts, NOCs.' },
      { step: '02', title: 'Legal Due Diligence', desc: 'Thorough verification of ownership chain, pending dues, and RERA compliance.' },
      { step: '03', title: 'Agreement Drafting', desc: 'Customised sale/purchase agreement protecting both buyer and seller interests.' },
      { step: '04', title: 'Stamp Duty & Registration', desc: 'Calculation of applicable stamp duty, booking of sub-registrar appointment, and full accompaniment on registration day.' },
      { step: '05', title: 'Post-Registration Work', desc: 'Mutation, society NOC, property tax transfer, and utility name change.' },
    ],
    testimonials: [
      { name: 'Deepak Rawat', location: 'Registered villa in Noida Extension', text: '"The legal team caught a discrepancy in the seller\'s title documents that would have cost me dearly. BricksNHome saved me from a fraudulent transaction."' },
      { name: 'Shobha Rao', location: 'Resale flat registration', text: '"The entire registration process was handled by their team. I just had to show up at the registrar office. Everything was pre-prepared perfectly."' },
    ],
    cta: 'Get Legal Assistance',
    stats: [{ n: '1,200+', l: 'Registrations Done' }, { n: '0', l: 'Title Disputes' }, { n: '100%', l: 'RERA Verified' }, { n: '48 hrs', l: 'Due Diligence Time' }],
  },
};

/* Map URL slug → service data */
const slugMap = {
  'buy-sell-property':    serviceData['buy-sell-property'],
  'rent-lease-property':  serviceData['rent-lease-property'],
  'commercial-properties':serviceData['commercial-properties'],
  'investment-advisory':  serviceData['investment-advisory'],
  'home-loan-assistance': serviceData['home-loan-assistance'],
  'legal-registration':   serviceData['legal-registration'],
};

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const s = slugMap[slug];

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!s) {
    return (
      <div className="sdp-not-found">
        <h2>Service not found</h2>
        <Link to="/services">← Back to Services</Link>
      </div>
    );
  }

  return (
    <div className="sdp-page">

      {/* ── HERO ── */}
      <div className="sdp-hero" style={{ background: s.heroColor }}>
        <img src={s.image} alt={s.title} className="sdp-hero-img" />
        <div className="sdp-hero-overlay" />
        <div className="sdp-hero-content">
          <Link to="/services" className="sdp-back">
            <i className="fa fa-arrow-left" /> All Services
          </Link>
          <div className="sdp-hero-icon"><i className={`fa ${s.icon}`} /></div>
          <h1>{s.title}</h1>
          <p className="sdp-tagline">{s.tagline}</p>
        </div>
      </div>

      {/* ── STATS BAR ── */}
      <div className="sdp-stats-bar">
        {s.stats.map((st, i) => (
          <div className="sdp-stat" key={i}>
            <strong>{st.n}</strong>
            <span>{st.l}</span>
          </div>
        ))}
      </div>

      <div className="sdp-body">

        {/* ── INTRO ── */}
        <section className="sdp-section sdp-intro-section">
          <div className="sdp-trust-badge">
            <i className="fa fa-shield-halved" /> Trusted by 8,500+ Clients · RERA Registered · 15 Years Experience
          </div>
          <p className="sdp-intro-text">{s.intro}</p>
        </section>

        {/* ── WHY CHOOSE US ── */}
        <section className="sdp-section">
          <div className="sdp-section-label">Why Choose Us</div>
          <h2 className="sdp-section-title">What Makes BricksNHome Different</h2>
          <div className="sdp-why-grid">
            {s.whyUs.map((w, i) => (
              <div className="sdp-why-card" key={i}>
                <div className="sdp-why-emoji">{w.icon}</div>
                <h4>{w.title}</h4>
                <p>{w.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── PROCESS ── */}
        <section className="sdp-section sdp-process-section">
          <div className="sdp-section-label">How It Works</div>
          <h2 className="sdp-section-title">Our Step-by-Step Process</h2>
          <div className="sdp-process-track">
            {s.process.map((p, i) => (
              <div className="sdp-process-step" key={i}>
                <div className="sdp-step-num">{p.step}</div>
                {i < s.process.length - 1 && <div className="sdp-step-line" />}
                <div className="sdp-step-body">
                  <h4>{p.title}</h4>
                  <p>{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── TESTIMONIALS ── */}
        <section className="sdp-section sdp-testi-section">
          <div className="sdp-section-label">Client Stories</div>
          <h2 className="sdp-section-title">What Our Clients Say</h2>
          <div className="sdp-testi-grid">
            {s.testimonials.map((t, i) => (
              <div className="sdp-testi-card" key={i}>
                <div className="sdp-testi-stars">★★★★★</div>
                <p className="sdp-testi-text">{t.text}</p>
                <div className="sdp-testi-author">
                  <div className="sdp-testi-avatar">
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <strong>{t.name}</strong>
                    <span>{t.location}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="sdp-cta-section">
          <div className="sdp-cta-box">
            <div className="sdp-cta-left">
              <h3>Ready to Get Started?</h3>
              <p>Speak to our expert team today — no obligation, no pressure.</p>
            </div>
            <div className="sdp-cta-right">
              <a href="tel:+917477471006" className="sdp-cta-call">
                <i className="fa fa-phone" /> +91 7477-471-006
              </a>
              <button className="sdp-cta-btn" onClick={() => navigate('/contact')}>
                {s.cta} <i className="fa fa-arrow-right" />
              </button>
            </div>
          </div>
          <div className="sdp-trust-strip">
            <span><i className="fa fa-shield-halved" /> RERA Registered</span>
            <span><i className="fa fa-star" /> 4.9/5 Rating</span>
            <span><i className="fa fa-users" /> 8,500+ Happy Clients</span>
            <span><i className="fa fa-clock" /> 15+ Years Experience</span>
            <span><i className="fa fa-handshake" /> Zero Hidden Charges</span>
          </div>
        </section>

      </div>
    </div>
  );
}
